import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/auth-context';
import { authFetch } from '@/utils/auth-fetch';
import {
  Image,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Building2,
  Sparkles,
  Clock,
  Camera,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface GenerationStatus {
  personas: {
    total: number;
    with_images: number;
    missing_images: number;
  };
  sectors: {
    total: number;
    with_images: number;
    missing_images: number;
  };
}

interface GenerationResult {
  personaId?: string;
  sectorId?: string;
  name: string;
  success: boolean;
  skipped?: boolean;
  reason?: string;
  imageUrl?: string;
  error?: string;
}

interface Site {
  id: string;
  name: string;
  url: string;
}

export default function ImageGeneration() {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [status, setStatus] = useState<GenerationStatus | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationType, setGenerationType] = useState<'personas' | 'sectors' | null>(null);
  const [regenerateAll, setRegenerateAll] = useState(false);
  const [results, setResults] = useState<GenerationResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSites();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedSiteId) {
      fetchStatus();
    }
  }, [selectedSiteId]);

  const fetchSites = async () => {
    try {
      const response = await authFetch('/api/sites');
      if (response.ok) {
        const data = await response.json();
        setSites(data.sites || []);
        if (data.sites?.length > 0) {
          setSelectedSiteId(data.sites[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchStatus = async () => {
    if (!selectedSiteId) return;

    try {
      const response = await authFetch(`/api/image-generation/status/${selectedSiteId}`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const handleGenerate = async (type: 'personas' | 'sectors') => {
    setGenerationType(type);
    setConfirmDialog(true);
  };

  const confirmGeneration = async () => {
    setConfirmDialog(false);
    if (!generationType) return;

    setGenerating(true);
    setResults([]);
    setProgress(0);

    try {
      const endpoint = generationType === 'personas'
        ? '/api/image-generation/personas'
        : '/api/image-generation/sectors';

      const body = generationType === 'personas'
        ? { siteId: selectedSiteId, regenerateAll }
        : { regenerateAll };

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data.results);

        // Calculate progress
        const total = data.stats.total;
        const completed = data.stats.successful + data.stats.skipped + data.stats.failed;
        setProgress((completed / total) * 100);

        // Show success message
        toast.success(
          `Generated ${data.stats.successful} images, skipped ${data.stats.skipped}, failed ${data.stats.failed}`
        );

        // Refresh status
        fetchStatus();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Generation failed');
      }
    } catch (error) {
      console.error('Error generating images:', error);
      toast.error('Failed to generate images');
    } finally {
      setGenerating(false);
      setGenerationType(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This page is only accessible to super administrators.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">AI Image Generation</h1>
        <p className="text-muted-foreground">
          Generate photo-realistic images for buyer personas and business sectors using Leonardo.ai
        </p>
      </div>

      {/* Site Selector */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Site</CardTitle>
          <CardDescription>
            Choose a site to manage its persona and sector images
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map(site => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name} - {site.url}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {status && (
        <Tabs defaultValue="personas" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="personas">
              <Users className="h-4 w-4 mr-2" />
              Buyer Personas
            </TabsTrigger>
            <TabsTrigger value="sectors">
              <Building2 className="h-4 w-4 mr-2" />
              Business Sectors
            </TabsTrigger>
          </TabsList>

          {/* Personas Tab */}
          <TabsContent value="personas" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Buyer Personas
                  </span>
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {status.personas.with_images} / {status.personas.total}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Generate photo-realistic images for buyer personas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{status.personas.total}</div>
                    <div className="text-sm text-muted-foreground">Total Personas</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {status.personas.with_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Have Images</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {status.personas.missing_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Missing Images</div>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <Button
                    onClick={() => {
                      setRegenerateAll(false);
                      handleGenerate('personas');
                    }}
                    disabled={generating || status.personas.missing_images === 0}
                    variant="default"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Generate Missing ({status.personas.missing_images})
                  </Button>

                  <Button
                    onClick={() => {
                      setRegenerateAll(true);
                      handleGenerate('personas');
                    }}
                    disabled={generating}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate All ({status.personas.total})
                  </Button>
                </div>

                {generating && generationType === 'personas' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Generating images...</span>
                      <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results for Personas */}
            {results.length > 0 && generationType === 'personas' && (
              <Card>
                <CardHeader>
                  <CardTitle>Generation Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : result.skipped ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="flex-1">{result.name}</span>
                        {result.imageUrl && (
                          <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            View Image
                          </a>
                        )}
                        {result.reason && (
                          <span className="text-xs text-muted-foreground">{result.reason}</span>
                        )}
                        {result.error && (
                          <span className="text-xs text-red-500">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Sectors Tab */}
          <TabsContent value="sectors" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Business Sectors
                  </span>
                  <Badge variant="outline" className="text-lg px-3 py-1">
                    {status.sectors.with_images} / {status.sectors.total}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Generate professional images for business sectors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{status.sectors.total}</div>
                    <div className="text-sm text-muted-foreground">Total Sectors</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {status.sectors.with_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Have Images</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {status.sectors.missing_images}
                    </div>
                    <div className="text-sm text-muted-foreground">Missing Images</div>
                  </div>
                </div>

                <div className="flex gap-4 items-center">
                  <Button
                    onClick={() => {
                      setRegenerateAll(false);
                      handleGenerate('sectors');
                    }}
                    disabled={generating || status.sectors.missing_images === 0}
                    variant="default"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Generate Missing ({status.sectors.missing_images})
                  </Button>

                  <Button
                    onClick={() => {
                      setRegenerateAll(true);
                      handleGenerate('sectors');
                    }}
                    disabled={generating}
                    variant="outline"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate All ({status.sectors.total})
                  </Button>
                </div>

                {generating && generationType === 'sectors' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Generating images...</span>
                      <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results for Sectors */}
            {results.length > 0 && generationType === 'sectors' && (
              <Card>
                <CardHeader>
                  <CardTitle>Generation Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {results.map((result, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        {result.success ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : result.skipped ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <span className="flex-1">{result.name}</span>
                        {result.imageUrl && (
                          <a
                            href={result.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            View Image
                          </a>
                        )}
                        {result.reason && (
                          <span className="text-xs text-muted-foreground">{result.reason}</span>
                        )}
                        {result.error && (
                          <span className="text-xs text-red-500">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {regenerateAll ? 'Regenerate All' : 'Generate Missing'} {generationType === 'personas' ? 'Persona' : 'Sector'} Images
            </DialogTitle>
            <DialogDescription>
              {regenerateAll
                ? `This will regenerate ALL ${generationType} images, replacing existing ones. This process may take several minutes.`
                : `This will generate images only for ${generationType} that don't have images yet.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                Images will be generated using Leonardo.ai with photo-realistic quality and automatically uploaded to the CDN.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmGeneration}>
              <Sparkles className="h-4 w-4 mr-2" />
              Start Generation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}